export default class CarouselComponent extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.reel = this.querySelector("[data-carousel-target='reel']");
        this.slides = Array.from(this.reel.children);
        this.previousArrow = this.querySelector("[data-carousel-target='flecha'][data-carousel-action='anterior']");
        this.nextArrow = this.querySelector("[data-carousel-target='flecha'][data-carousel-action='siguiente']");
        this.pauseButton = this.querySelector("[data-carousel-target='pausa']");
        this.dots = Array.from(this.querySelectorAll("[data-carousel-target='punto']"));
        this.configure();
    }

    configure() {
        this.singleSlide = this.hasAttribute("single");
        this.autoScroll = !!this.pauseButton
                && !window.matchMedia(`(prefers-reduced-motion: reduce)`).matches;
        this.loop = this.autoScroll || !this.hasAttribute("no-loop");
        this.currentIndex = 0;
        this.slidesOnScreen = this.visibleSlides;
        this.slideFromDot = this.dotClickCallback();
        this.slideFromArrow = this.arrowClickCallback();
        this.keyboardControls = this.carouselKeydownCallback();
        this.resizeReel = this.carouselResizeCallback();
        this.resizeObserver = new ResizeObserver(this.resizeReel);
        this.resizeObserver.observe(this);
        this.slides.forEach(slide => slide.tabIndex = 0);
        this.dots.forEach((dot, index) => {
            dot.setAttribute("aria-pressed", index === 0 ? "true" : "false");
            dot.slideIndex = index;
            dot.addEventListener("click", this.slideFromDot);
            dot.update = this.dotSlideCallback(dot);
        });
        if (!!this.previousArrow) {
            this.previousArrow.update = this.arrowSlideCallback(this.previousArrow);
            this.previousArrow.addEventListener("click", this.slideFromArrow);
        }
        if (!!this.nextArrow) {
            this.nextArrow.update = this.arrowSlideCallback(this.nextArrow);
            this.nextArrow.addEventListener("click", this.slideFromArrow);
        }
        if (!!this.pauseButton) {
            this.setInterval = this.setAutoScrollInterval();
            this.clearInterval = this.clearAutoScrollInterval();
            this.changeVisibilityPause = this.visibilityChangeCallback();
            this.changePauseState = this.pauseButtonClickCallback();
            this.pauseButton.addEventListener("click", this.changePauseState);
            this.addEventListener("pointerenter", this.clearInterval);
            this.addEventListener("pointerleave", this.setInterval);
            this.addEventListener("focusin", this.clearInterval);
            this.addEventListener("focusout", this.setInterval);
            document.addEventListener("visibilitychange", this.changeVisibilityPause);
        }
        this.addEventListener("keydown", this.keyboardControls);
        this.update();
        this.setInterval();
    }

    slideTo(behavior) {
        const orientation = (this.hasAttribute("vertical") ? "Top" : "Left"),
                offsetMode = `offset${orientation}`,
                newSlideScroll = this.slides[this.currentIndex][offsetMode],
                reelReference = this.reel[offsetMode],
                scroll = newSlideScroll - reelReference;

        this.reel.scrollTo({[
                    orientation.toLowerCase()]: scroll, behavior});
        this.update();
    }

    update() {
        this.previousArrow?.update();
        this.nextArrow?.update();
        for (const dot of this.dots) {
            dot.update();
        }
        //this.setInterval()
    }

    setAutoScrollInterval() {
        return () => {
            const isPaused = this.pauseButton?.getAttribute("aria-pressed") === "true";

            if (!this.autoScroll || isPaused || !!this.interval) {
                return;
            }

            this.interval = setInterval(() => {
                this.currentIndex = this.nextSlideIndex;
                this.slideTo("smooth");
            }, 5000);
            this.pauseButton?.setAttribute("aria-pressed", "false"); // mixed
        };
    }

    clearAutoScrollInterval() {
        return () => {
            const isPaused = this.pauseButton?.getAttribute("aria-pressed") === "true";

            if (!!this.interval) {
                clearInterval(this.interval);
                delete this.interval;
                if (!isPaused) {
                    this.pauseButton?.setAttribute("aria-pressed", "mixed"); // false
                }
            }
        };
    }

    carouselKeydownCallback() {
        return (event) => {
            const {key} = event;

            switch (key) {
                case "p":
                case "Pause":
                    event.preventDefault();
                    if (!this.autoScroll) {
                        return;
                    }

                    return this.changePauseState();
                case "Home":
                    event.preventDefault();
                    this.currentIndex = 0;
                    break
                case "End":
                    event.preventDefault();
                    this.currentIndex = this.slides.length - 1;
                    break
                case "PageUp":
                    event.preventDefault();
                    this.currentIndex = this.previousSlideIndex;
                    break
                case "PageDown":
                    event.preventDefault();
                    this.currentIndex = this.nextSlideIndex;
                    break
                case "ArrowLeft":
                    event.preventDefault();
                    this.currentIndex = Math.max(0, this.currentIndex - 1);
                    break
                case "ArrowRight":
                    event.preventDefault();
                    this.currentIndex = Math.min(this.currentIndex + 1, this.slides.length - 1);
                    break
                default:
                    return;
            }
            document.activeElement.blur();
            this.slides[this.currentIndex].focus();
            this.slideTo("smooth");
        };
    }

    carouselResizeCallback() {
        return (entries) => {
            const currentVisibleSlides = this.visibleSlides;

            if (currentVisibleSlides !== this.slidesOnScreen) {
                this.slidesOnScreen = currentVisibleSlides;
                this.currentIndex = 0;
            }

            this.slideTo("instant");
        };
    }

    arrowSlideCallback(arrow) {
        if (arrow === this.previousArrow) {
            return () => {
                arrow.slideIndex = this.previousSlideIndex;
                arrow.disabled = !this.loop && currentIndex === 0;
            };
        }

        if (arrow === this.nextArrow) {
            return () => {
                arrow.slideIndex = this.nextSlideIndex;
                arrow.disabled = !this.loop && currentIndex === maxSlideIndex;
            };
        }

        return;
    }

    arrowClickCallback() {
        return (event) => {
            const {currentTarget: arrow} = event;
            this.currentIndex = arrow.slideIndex;
            this.slideTo("smooth");
        };
    }

    dotClickCallback() {
        return (event) => {
            const {currentTarget} = event,
            actualDot = this.dots.find(dot => dot.getAttribute("aria-pressed") === "true");

            if (actualDot === currentTarget) {
                return;
            }

            this.currentIndex = currentTarget.slideIndex;
            this.slideTo("smooth");
        };
    }

    dotSlideCallback(dot) {
        return () => {
            const {slideIndex} = dot,
            {maxSlideIndex} = this;

            dot.hidden = !(slideIndex % this.slidesOnScreen === 0 && slideIndex < maxSlideIndex)
                    && slideIndex !== maxSlideIndex;
            dot.setAttribute("aria-pressed", slideIndex === this.currentIndex ? "true" : "false");
        };
    }

    pauseButtonClickCallback() {
        return () => {
            const pressedState = this.pauseButton.getAttribute("aria-pressed");
            this.pauseButton.setAttribute("aria-pressed", pressedState !== "true" ? "true" : "mixed");
            this.clearInterval();
        };
    }

    visibilityChangeCallback() {
        return () => {
            const {visibilityState} = document;
            if (visibilityState === "visible") {
                this.setInterval();
            } else {
                this.clearInterval();
            }
        };
    }

    get visibleSlides() {
        const {offsetWidth: reelWidth, offsetHeight: reelHeight} = this.reel,
        {offsetWidth: slideWidth, offsetHeight: slideHeight} = this.slides[0],
        horizontalSlides = Math.round(reelWidth / slideWidth),
                verticalSlides = Math.round(reelHeight / slideHeight);

        return horizontalSlides * verticalSlides;
    }

    get maxSlideIndex() {
        return Math.max(0, this.slides.length - this.slidesOnScreen);
    }

    get nextSlideIndex() {
        const {maxSlideIndex, currentIndex} = this,
        updatedIndex = currentIndex + (this.singleSlide ? 1 : this.slidesOnScreen);

        if (currentIndex === maxSlideIndex) {
            return 0;
        }

        return Math.min(maxSlideIndex, updatedIndex);
    }

    get previousSlideIndex() {
        const {maxSlideIndex, currentIndex, slidesOnScreen} = this,
        updatedIndex = currentIndex - (this.singleSlide ? 1 : slidesOnScreen);

        if (currentIndex === 0) {
            return maxSlideIndex;
        }
        if (currentIndex === maxSlideIndex) {
            const indexDifference = maxSlideIndex % slidesOnScreen === 0
                    ? slidesOnScreen
                    : (this.slides.length % slidesOnScreen);
            return maxSlideIndex - indexDifference;
        }

        return Math.max(0, updatedIndex);
    }
}

